// COMMS-LOG-8 (2026-08-28) — the call lives above the page.
//
// Mark: "be good to have a way that users can be on a call and go to the
// application page to take notes etc or view docs but still be able to end
// call". That is not a UI tweak — it is an ownership change.
//
// Until now the Twilio Device and the active call were owned by DiallerPanel.
// Anything owned by a panel dies when that panel unmounts, and every admin
// screen here is a conditional `activeTab === ...` render, so navigating to
// the deal you are ON THE PHONE ABOUT would have dropped the call. Which is
// exactly the moment a broker needs the deal open.
//
// So the active call lives here, mounted above the router in App.js. Panels
// become consumers: they ask to start a call, they read the current one, and
// the call itself is unaffected by whatever page is showing.
//
// COMMS-RETIRE-1 (2026-09-03) — the browser dialler was deleted along with the
// rest of the in-CRM Twilio module: it registered against a TwiML App on the
// CRM's own Twilio project, which owns no numbers and never will again.
//
// SOFTPHONE-1 (2026-09-14) — and now it is back, on the right foundation. The
// Device below registers against the COMMS platform's Twilio account, under the
// same `twilio_identity` the SOLCOM app uses. That single detail is what makes
// this work: Twilio forks one `client:<identity>` leg to every endpoint
// registered for that identity, so the desk and the handset ring together and
// whichever answers first takes it — verified on real devices on 14 Sep (one
// leg, browser over websocket, iPhone over PushKit, loser gets `cancel`).
//
// SOFTPHONE-5 (2026-09-14) — and now it dials out itself, which SOFTPHONE-1
// deliberately did not do. That note used to end "no second outbound path", and
// that was the right call for one day: click-to-call already rang this browser,
// so answering the leg we had just asked for was the smallest thing that worked.
//
// It is the wrong shape for a call you PLACE. comms rings `client:<identity>`,
// which forks to every endpoint, so pressing Call on a laptop also rang the phone
// in your pocket and made you answer your own outgoing call before it dialled the
// client. Mark: "default should be via the method you are logged into or using".
//
// So there is a second outbound path now, and it is the one SOLCOM has always
// used — `device.connect()` against the same TwiML Application (comms-app
// TwilioCallingService `voice.connect`). comms' /twilio/voice/client-outbound
// builds the SAME `calls` row click-to-call builds, so filing, auto-filing,
// outcomes, webhooks and the unfiled queue are untouched.
//
// Click-to-call remains for the case that still needs it — no softphone, or a
// refused microphone — and then the handset rings exactly as it did. That
// fallback is still the feature.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { loadVoiceSdk, mintVoiceToken, openCommsSession } from "@/lib/commsSoftphone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallContext = createContext(null);

// comms' own vocabulary for a call that is over (comms-api src/lib/types.ts
// FINAL_CALL_STATUSES). Anything else means it is still going.
const FINAL_CALL_STATUSES = new Set(["completed", "busy", "no-answer", "failed", "canceled"]);
const CALL_POLL_MS = 4000;
// A poll that keeps failing must not spin forever behind a bar nobody can see is
// stuck. After this many consecutive failures the bar stays up for manual dismissal
// — the honest outcome when we genuinely cannot tell what the call is doing.
const CALL_POLL_MAX_FAILURES = 5;

export function useCall() {
  // Returns null outside the provider (e.g. client portal routes), so callers
  // can feature-detect rather than crash. PhoneLink relies on this.
  return useContext(CallContext);
}

export function CallProvider({ children }) {
  // SOFTPHONE-2 (2026-09-14) — registration waits for the signed-in user.
  //
  // App.js has always said this provider "reads the signed-in user to decide whether to
  // register a Twilio Device at all, so it sits inside AuthProvider". SOFTPHONE-1 did not
  // actually do that, and the result was a silent, permanent no-op in production: the
  // registration effect ran on mount, which is BEFORE AuthProvider sets
  // `axios.defaults.headers.common.Authorization`, so the ticket request went out with no
  // credentials, came back 401, and the effect — which runs once — never tried again.
  // Nothing broke, nothing logged, and click-to-call kept ringing the handset, which is
  // precisely why it took a production packet capture to notice.
  //
  // `isStaff` is the right gate rather than `token`: it is only true once /auth/me has
  // returned, so the header is certainly set by then, and a client-portal user never asks
  // comms for a ticket it would refuse anyway.
  const { isStaff } = useAuth();

  // The live call, or null. `who` is the display name, `number` the E.164.
  const [active, setActive] = useState(null);
  const [seconds, setSeconds] = useState(0);

  // COMMS-LOG-9 — the call that just ended, kept so a note still has
  // somewhere to go. Mark: "if the user is still typing a note but ends the
  // call the panel disappears before they finish". Calls end mid-sentence all
  // the time — the other side hangs up while you're writing what was agreed —
  // and losing that note is losing the single most valuable thing about
  // having taken it. The bar stays up on this until the note is saved or
  // explicitly discarded.
  const [lastEnded, setLastEnded] = useState(null);

  // SOFTPHONE-1 — "can this browser carry audio right now", for the UI to show
  // and for startCall to reason about. `off` is a perfectly good state.
  const [softphone, setSoftphone] = useState({ status: "off", identity: null, error: null });

  const timerRef = useRef(null);
  const deviceRef = useRef(null);
  const sessionRef = useRef(null);
  const twilioCallRef = useRef(null);
  // The call_id of a click-to-call we just asked for, so that when the matching
  // leg rings this browser we answer it instead of presenting it as an incoming
  // call from the person we are in the middle of ringing.
  const pendingCallIdRef = useRef(null);

  /**
   * End the call locally and remember it, so a note still has somewhere to go.
   *
   * SOFTPHONE-3 (2026-09-14) — `connected` is the new part, and it exists because the bar
   * now disappears on its own. Before COMMS-APPRING-1 and the softphone, nothing ever ended
   * a call from the CRM's side: the bar sat there until the broker dismissed it, so there
   * was always time to open the note box. Now the call really does end — the other side
   * hangs up, or you do — and the bar went with it, taking the note box before anyone had
   * touched it. ActiveCallBar uses this to keep itself up after a call that actually
   * happened. A call that merely rang is not worth a sticky bar.
   *
   * Callers that know better than the local status pass it explicitly: the status poll has
   * comms' own `answered_at`, which is authoritative for a call answered on the handset and
   * survives a short call the 4s poll never saw as `in-progress`.
   */
  const clear = useCallback((opts = {}) => {
    setActive((a) => {
      if (a) {
        setLastEnded({
          number: a.number,
          who: a.who,
          applicationId: a.applicationId,
          connected: opts.connected ?? a.status === "live",
        });
      }
      return null;
    });
    setSeconds(0);
    twilioCallRef.current = null;
    pendingCallIdRef.current = null;
  }, []);

  /** Called once the note is saved or the user discards it. */
  const dismissLastEnded = useCallback(() => setLastEnded(null), []);

  /**
   * Microphone access, requested lazily.
   *
   * Not requested at registration: a getUserMedia prompt fired on page load is
   * deferred by Chrome while the tab is in the background, which looks exactly
   * like a hang (it cost us twenty minutes on 14 Sep). Asking on the first call
   * means asking during a user gesture, when a prompt is expected. The grant is
   * per-origin and persists, so this is a one-time interruption.
   */
  const ensureMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch {
      toast.error("Your browser blocked the microphone, so this call will ring your phone instead.");
      return false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Register this browser as an endpoint for the signed-in broker.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isStaff) {
      // Signed out, still loading, or a client-portal user. Tear down any Device from a
      // previous session rather than leaving this browser registered as someone who has
      // logged out — it would keep ringing for their calls.
      setSoftphone({ status: "off", identity: null, error: null });
      return undefined;
    }
    let cancelled = false;
    let device = null;

    (async () => {
      setSoftphone((s) => ({ ...s, status: "connecting" }));
      let Device;
      let voice;
      try {
        const session = await openCommsSession();
        if (cancelled) return;
        sessionRef.current = session;
        voice = await mintVoiceToken(session);
        sessionRef.current = voice.session;
        Device = await loadVoiceSdk();
      } catch (err) {
        // Every failure here is survivable: no comms membership, a key without
        // the session:mint scope, comms unreachable. Click-to-call keeps
        // working and rings the handset, so this is a downgrade, not an outage.
        //
        // It is logged, though. A softphone that silently declines to exist is
        // indistinguishable from one nobody has switched on, and that cost real time on
        // 14 Sep — twice, once for CORS and once for the auth ordering above.
        if (!cancelled) {
          console.warn("[softphone] not registering:", err?.message || err);
          setSoftphone({ status: "off", identity: null, error: err?.message || String(err) });
        }
        return;
      }
      if (cancelled) return;

      device = new Device(voice.token, { codecPreferences: ["opus", "pcmu"] });
      deviceRef.current = device;

      device.on("registered", () => {
        if (!cancelled) setSoftphone({ status: "ready", identity: voice.identity, error: null });
      });
      device.on("unregistered", () => {
        if (!cancelled) setSoftphone((s) => ({ ...s, status: "off" }));
      });
      device.on("error", (err) => {
        if (!cancelled) setSoftphone((s) => ({ ...s, error: err?.message || String(err) }));
      });

      // The Twilio token is good for an hour; the comms access token behind it
      // for fifteen minutes. mintVoiceToken renews the latter when it needs to,
      // so this stays a one-liner.
      device.on("tokenWillExpire", async () => {
        try {
          const next = await mintVoiceToken(sessionRef.current);
          sessionRef.current = next.session;
          device.updateToken(next.token);
        } catch {
          // Leave the existing token to expire; `error` will surface it and the
          // handset still rings.
        }
      });

      device.on("incoming", (call) => {
        const params = Object.fromEntries(call.customParameters || []);
        // A leg we asked for moments ago, rather than somebody calling in. comms tags
        // the click-to-call leg with direction=outbound and the call_id (comms-api #16).
        //
        // Since SOFTPHONE-5 this only happens on the click-to-call fallback: a call this
        // browser places itself never rings back, so there is no leg to recognise. It still
        // matters, because a browser with no microphone is a registered endpoint all the
        // same and comms will ring it for the call we just asked for.
        const ours = params.direction === "outbound"
          && !!params.call_id
          && params.call_id === pendingCallIdRef.current;

        twilioCallRef.current = call;

        call.on("accept", () => {
          // From here this browser is carrying the audio. `carriedHere` stands the
          // status poll down: the Twilio events below are faster and exact.
          setActive((a) => (a ? { ...a, status: "live", carriedHere: true } : a));
        });

        call.on("disconnect", () => { twilioCallRef.current = null; clear(); });

        // `cancel` means this endpoint lost the race — nearly always because the
        // broker answered on their handset instead.
        //
        // For a call WE placed that is not the end of anything: it is live on the
        // phone, the bar must stay up, and the status poll — which never stopped,
        // because `carriedHere` is still false — follows it to the end. Clearing here
        // would put back precisely the bug COMMS-APPRING-1 was written to fix.
        //
        // For a call ringing in, this browser simply is not part of it, and the CRM
        // has no call id to follow, so the bar goes.
        const releaseUnlessOurs = () => {
          twilioCallRef.current = null;
          if (!ours) clear();
        };
        call.on("cancel", releaseUnlessOurs);
        call.on("reject", releaseUnlessOurs);

        if (ours) {
          // SOFTPHONE-4 (2026-09-14) — ring, do NOT auto-answer.
          //
          // This used to call `call.accept()` immediately, on the reasoning that the user had
          // just pressed Call so answering was what they asked for. That was wrong, and Mark
          // caught it on the first real use: "the call only calls on the laptop not solcom".
          // Accepting within milliseconds wins the race against every other endpoint, so
          // Twilio cancels the handset before it has audibly rung. The brief for this whole
          // piece of work was "the browser and the iPhone both ringing so I can answer on
          // either" — auto-answering quietly deleted the choice.
          //
          // So the leg is presented here and the handset keeps ringing. Whichever is answered
          // first wins, which is the behaviour Twilio gives us for free and the one that was
          // asked for.
          setActive((a) => (a ? { ...a, status: "incoming", outbound: true } : a));
          return;
        }

        setActive({
          number: params.from || call.parameters?.From || "",
          who: params.displayName || params.from || "Incoming call",
          applicationId: null,
          communicationId: null,
          callId: params.call_id || null,
          carriedHere: false,
          status: "incoming",
        });
      });

      try {
        await device.register();
      } catch (err) {
        if (!cancelled) {
          console.warn("[softphone] register failed:", err?.message || err);
          setSoftphone({ status: "off", identity: null, error: err?.message || String(err) });
        }
      }
    })();

    return () => {
      cancelled = true;
      try { device?.destroy(); } catch { /* already gone */ }
      deviceRef.current = null;
    };
  }, [clear, isStaff]);

  /** Answer a call ringing this browser. */
  const answer = useCallback(async () => {
    const call = twilioCallRef.current;
    if (!call) return;
    if (!(await ensureMic())) return;
    call.accept();
  }, [ensureMic]);

  /** Decline it here. The handset keeps ringing if it is also registered. */
  const decline = useCallback(() => {
    const call = twilioCallRef.current;
    if (call) call.reject();
    twilioCallRef.current = null;
    clear();
  }, [clear]);

  /**
   * End the call.
   *
   * When this browser is carrying the audio there is a real connection to drop.
   * When the call was answered on the handset there is not — the CRM only ever
   * had its own idea of it — and that remains true, so this still clears local
   * state in that case rather than pretending to hang up.
   */
  const hangUp = useCallback(() => {
    const call = twilioCallRef.current;
    if (call) {
      try { call.disconnect(); } catch { /* already gone */ }
      twilioCallRef.current = null;
    }
    clear();
  }, [clear]);

  /**
   * The original path: comms rings this staff member — their app, then their forward-to
   * number — and bridges whoever answers to `number`.
   *
   * It is reached two ways now. Deliberately, when this browser cannot carry audio at all;
   * and as the safety net when it tried to and could not. `switched` only changes what the
   * toast says, because the two cases feel different to the person who pressed Call.
   */
  const ringThroughComms = useCallback(async (number, base, meta, { switched = false } = {}) => {
    setActive({ ...base, callId: null, carriedHere: false, status: "bridging" });
    try {
      const res = await axios.post(`${API}/comms/voice/click-to-call`, {
        to: number,
        contact_external_id: meta.contactExternalId || null,
      });
      const callId = res.data?.call_id || null;
      if (callId) {
        setActive((a) => (a ? { ...a, callId } : a));
        // Set unconditionally, which it was not before SOFTPHONE-5. Reaching here means this
        // browser cannot CARRY the call — but it is still a registered endpoint, so comms'
        // `client:<identity>` leg rings it anyway. Without this the `incoming` handler would
        // not recognise our own outgoing call and would present it as somebody ringing in,
        // mid-way through us ringing them.
        pendingCallIdRef.current = callId;
      }
      const ringOn = res.data?.calling_you_on;
      const where = ringOn ? `on ${ringOn}` : "now";
      toast.success(
        switched
          ? `Couldn't call from this browser — ringing you ${where} instead.`
          : `Ringing you ${where}…`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start that call.");
      clear();
    }
  }, [clear]);

  /**
   * Place a call — out of the device you are using, not back to it (SOFTPHONE-5).
   *
   * When this browser can carry audio it dials directly: `device.connect()` opens a
   * <Client> leg to the tenant's TwiML Application, which POSTs to comms'
   * /twilio/voice/client-outbound and <Dial>s the destination beneath us. Nothing else
   * rings, audio starts as soon as the far end answers, and there is no leg to go and
   * answer first.
   *
   * Otherwise it falls back to click-to-call exactly as before: comms rings this staff
   * member — their app, then their forward-to number — and bridges whoever answers.
   *
   * @param number  E.164 destination
   * @param meta    { name, applicationId, contactExternalId } —
   *                applicationId lets the note box and any later filing attach
   *                to the right deal without the user re-picking it.
   *                contactExternalId (COMMS-PLATFORM-1) names the CRM record
   *                for the comms platform (`user:<id>`, `lead:<id>`,
   *                `contact:<id>`); optional, comms matches by number without it.
   */
  const startCall = useCallback(async (number, meta = {}) => {
    if (!number) return;
    if (active) {
      toast.error("You're already on a call.");
      return;
    }

    const device = deviceRef.current;
    // Ask for the microphone before placing the call, not while it is ringing.
    // If it is refused we still place the call — it simply rings the handset.
    const canUseBrowser = softphone.status === "ready" && !!device && (await ensureMic());

    const base = {
      number,
      who: meta.name || number,
      applicationId: meta.applicationId || null,
      communicationId: null,
    };

    if (canUseBrowser) {
      // The call id is ours to choose here, because comms honours it
      // (comms-api createAppOutboundCall `requestedCallId`). That means the note box and
      // any later filing can address the row before the call has even connected, instead
      // of waiting for a response the way click-to-call has to.
      const callId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : null;
      // `carriedHere` is true from the start on this path, and that is the point: we hold
      // the actual Twilio connection, so its events are ground truth and the status poll
      // has nothing to add. On the click-to-call path below it stays false until this
      // browser accepts a leg, because until then the handset may take the call instead.
      setActive({ ...base, callId, carriedHere: true, status: "connecting" });
      try {
        const call = await device.connect({
          params: {
            To: number,
            ...(callId ? { CallId: callId } : {}),
            ...(meta.contactExternalId ? { ContactExternalId: meta.contactExternalId } : {}),
          },
        });
        twilioCallRef.current = call;

        // `accept` is this browser's own leg joining Twilio, NOT the far end picking up:
        // the TwiML <Dial> runs beneath us and you hear ringback until it bridges. So the
        // on-screen timer can start a few seconds early on this path. The duration that
        // matters — the one on the `calls` row — comes from comms' dial-result callback
        // and is unaffected. Fixing the display properly means `answerOnBridge="true"` on
        // that <Dial> in comms-api, which changes SOLCOM's timing too and wants a device
        // test before it ships.
        call.on("accept", () => setActive((a) => (a ? { ...a, status: "live" } : a)));
        call.on("disconnect", () => { twilioCallRef.current = null; clear(); });
        call.on("cancel", () => { twilioCallRef.current = null; clear(); });
        // No fallback from HERE, unlike the catch below. By this point the leg exists and
        // the TwiML may already have dialled the client, so quietly placing a second call
        // could ring them twice. An error a broker can see and act on beats a duplicate
        // call they cannot.
        call.on("error", (err) => {
          console.warn("[softphone] outbound call failed:", err?.message || err);
          toast.error("That call dropped. Try again, or ring them from your phone.");
          twilioCallRef.current = null;
          clear();
        });
      } catch (err) {
        // The Device refused to place it — an expired token, a lost websocket, a Device
        // that says it is ready and is not.
        //
        // Do NOT strand the user here. This is precisely the situation click-to-call
        // exists for, and before SOFTPHONE-5 they would simply have got a ringing handset.
        // Leaving them with an error toast and no call would make the new path strictly
        // worse than the old one in its failure case, which is not a trade worth making:
        // they asked to ring somebody, and where the audio lands matters far less than
        // that the call happens at all.
        console.warn("[softphone] could not place the call here, ringing the handset instead:", err?.message || err);
        await ringThroughComms(number, base, meta, { switched: true });
      }
      return;
    }

    await ringThroughComms(number, base, meta);
  }, [active, clear, ensureMic, ringThroughComms, softphone.status]);

  /**
   * CONF-1 (2026-09-15) — put a third person on this call.
   *
   * comms does the work: it redirects both existing legs into a Twilio conference and dials the
   * new number in. Two things follow from that which the UI has to be honest about — there is
   * about a second of silence as the call re-forms, and it only works once the call is actually
   * CONNECTED, because a call still ringing has no second leg to conference with.
   *
   * Deliberately no optimistic state. Until comms confirms, nobody has been added, and a
   * three-way call that silently was not one is worse than a slow button.
   */
  const addParticipant = useCallback(async (number) => {
    const to = (number || "").trim();
    const callId = active?.callId;
    if (!to || !callId) return false;
    try {
      const res = await axios.post(`${API}/comms/voice/call/${callId}/participants`, { to });
      const added = res.data?.added || [];
      setActive((a) => (a ? { ...a, participants: added } : a));
      toast.success("Adding them now — you'll both hear a short gap.");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add them to the call.");
      return false;
    }
  }, [active?.callId]);

  /**
   * Save a note against the call. Falls back to the most recent
   * communication for this number when we don't yet know the id — a
   * click-to-call bridge has no id client-side until the webhook lands, and
   * losing a broker's note because of that timing would be unforgivable.
   */
  // COMMS-LOG-10b (2026-08-29) — `silent` exists because of a real trap.
  // Mark, on a live call: "if i have a note the confirmation pop up blocks me
  // from ending the call". The success toast lands top-right, which is exactly
  // where ActiveCallBar's red Hang up button is — so confirming the note took
  // away the one control you cannot afford to lose while the client is still
  // on the line and the meter is running.
  //
  // The bar already renders its own inline "Saved ✓", so the toast was pure
  // duplication on that surface. Callers that have their own confirmation pass
  // silent; the ERROR toast always fires, because a failed save with no
  // feedback is how a note gets lost.
  const saveNote = useCallback(async (text, { silent = false } = {}) => {
    const note = (text || "").trim();
    if (!note) return false;
    // Falls back to the call that just ended — the whole point of keeping
    // `lastEnded` is that a note finished after hang-up still lands.
    const number = active?.number || lastEnded?.number;
    if (!number) return false;
    try {
      // CALL-NOTE-1 (2026-09-14) — the note always saves now. `pending` means it had no call
      // record to attach to yet: either the status callback is seconds away and the webhook
      // will file it, or the number belongs to nothing and it is waiting in Unfiled notes.
      // Either way it is stored, which is the part that used to fail.
      const res = await axios.post(`${API}/comms/note`, { number, note });
      const pending = !!res.data?.pending;
      if (!silent) toast.success(pending ? "Note saved — filing it to the call." : "Note saved to the deal.");
      return pending ? { ok: true, pending: true, noteId: res.data?.note_id } : true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that note.");
      return false;
    }
  }, [active?.number, lastEnded?.number]);

  const value = useMemo(() => ({
    // state
    active, seconds, lastEnded, softphone,
    // actions
    startCall, hangUp, saveNote, dismissLastEnded, answer, decline, addParticipant,
  }), [active, seconds, lastEnded, softphone, startCall, hangUp, saveNote, dismissLastEnded, answer, decline, addParticipant]);

  // COMMS-APPRING-1 (2026-09-14) — the bar learns when the call actually ends.
  //
  // Mark, on the first app-rung call: *"when I hung up the call on the handset the
  // call bar on the CRM stayed open with the hang up button."* It always had. The
  // CRM starts a call it holds no connection to, so nothing ever told it the call
  // was over and the bar sat there until someone dismissed it by hand.
  //
  // comms knows, because Twilio's status callbacks land there. So ask it. A webhook
  // would reach the CRM's backend rather than this browser tab, and a call is short
  // enough that a few seconds of lag costs nothing — so polling is the right shape
  // here, not a worse version of push.
  //
  // SOFTPHONE-1 (2026-09-14) — but NOT when this browser is carrying the call.
  // Then we hold the actual Twilio connection, and its `disconnect` fires the
  // instant the call ends: ground truth, with no lag and no request. Polling on top
  // of that would be four seconds slower, strictly less accurate, and a second voice
  // racing the first to call `clear()`. So the poll covers exactly the case it was
  // written for — a call answered somewhere this tab cannot see.
  useEffect(() => {
    const callId = active?.callId;
    if (!callId || active?.carriedHere) return undefined;
    let cancelled = false;
    let failures = 0;
    const handle = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/comms/voice/call/${callId}`);
        if (cancelled) return;
        failures = 0;
        if (FINAL_CALL_STATUSES.has(data?.status)) {
          // comms knows whether this was ever answered, which we may not: a call picked up
          // on the handset and finished inside one 4s poll window never shows as
          // `in-progress` here, but it still earned a note.
          clear({ connected: Boolean(data?.answered_at) });
          return;
        }
        if (data?.status === "in-progress") {
          setActive((a) => (a && a.status !== "live" ? { ...a, status: "live" } : a));
        }
      } catch {
        // Transient failures are expected — a deploy, a flaky moment. Keep the bar and
        // try again; a call that really has ended clears on the next good poll.
        failures += 1;
        if (failures >= CALL_POLL_MAX_FAILURES) clearInterval(handle);
      }
    }, CALL_POLL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, [active?.callId, active?.carriedHere, clear]);

  // Timer for a live call. Two things can promote a call to "live" now: the poll
  // above, when comms reports a call answered elsewhere, and the Twilio call's own
  // `accept` event when this browser answered it.
  useEffect(() => {
    if (active?.status !== "live") return undefined;
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };
  }, [active?.status]);

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
